import React, { useEffect, useRef, useState } from 'react';
import { TSizeVariants } from './global';
import { Utils } from './util';
import { NJIconButton, NJIcon, IButtonAnchorProps, IButtonButtonProps, NJButton } from '@engie-group/fluid-design-system-react';


const InputSearch = React.forwardRef<HTMLInputElement, IInputSearchProps>(
  (props, forwardedRef) => {
    const {
      onEnterKeyPress,
      onSearchChange,
      onChange,
      onClear,
      id,
      label,
      resetButtonTitle = 'clear',
      value,
      buttonOptions,
      scale,
      disabled = false,
      ...otherProps
    } = props;

    const [isFilled, setIsFilled] = useState(false);
    const [controlledValue, setControlledValue] = useState(value ?? '');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (value) setIsFilled(typeof value === 'number' || value.length > 0);
      setControlledValue(value ?? '');
    }, [value]);

    const clearInput = () => {
      setIsFilled(false);
      setControlledValue('');
    };

    const focusInput = () => {
      if (inputRef?.current) {
        inputRef?.current.focus();
      }
    };

    const onClearButtonClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      clearInput();
      focusInput();
      onClear?.();
    };

    const onChangeValue: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const newValue = e.currentTarget.value;
      setControlledValue(newValue ?? '');
      setIsFilled(newValue.length > 0);

      if (onSearchChange) {
        onSearchChange(e, controlledValue);
      }
      onChange?.(e);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === 'Enter') {
        if (typeof onEnterKeyPress === 'function') onEnterKeyPress(e, value);
      }

      props.onKeyDown?.(e);
    };

    const rootClass = 'nj-search';
    const searchClass = Utils.classNames(rootClass, {
      [`${rootClass}--${scale}`]: !!scale
    });

    const buttonClass = Utils.classNames(`${rootClass}__button`, buttonOptions?.className);

    return (
      <div className={searchClass}>
        <div className={`${rootClass}__field-wrapper`}>
          <NJIcon name="search" ariaLabel="searchIcon" className={`${rootClass}__icon`} />
          <label htmlFor={id} aria-hidden={true} className="nj-sr-only">
            {label}
          </label>
          <input
            {...otherProps}
            className={`${rootClass}__field`}
            type="search"
            id={id}
            value={controlledValue}
            onChange={onChangeValue}
            ref={Utils.mergeRefs([inputRef, forwardedRef])}
            disabled={disabled}
            onKeyDown={onKeyDown}
          />

          {isFilled && (
            <NJIconButton
              label={resetButtonTitle}
              icon="cancel"
              onClick={onClearButtonClick}
              className={`${rootClass}__clear-button`}
            />
          )}
        </div>

        {buttonOptions && (
          <NJButton
            {...buttonOptions}
            className={buttonClass}
          />
        )}
      </div>
    );
  }
);

export interface IInputSearchProps extends React.ComponentPropsWithoutRef<'input'> {
  /**
   * Label for the input search element
   */
  label: string;
  /**
   * Function called on input change
   * @param e event
   * @param value value
   * @deprecated Prefer using `onChange` prop
   */
  onSearchChange?: (e: React.ChangeEvent<HTMLElement>, value: IInputSearchProps['value']) => void;
  /**
   * Return search value on enter
   * */
  onEnterKeyPress?: (
    e: React.KeyboardEvent<HTMLInputElement>,
    value: IInputSearchProps['value']
  ) => void;
  /**
   * Function called when the clear button is clicked
   */
  onClear?: () => void;
  /**
   * Title of the reset button shown when the field is filled with text
   */
  resetButtonTitle?: string;
  /**
   * Options if you want to show a button next to the search input
   */
  buttonOptions?:
  | Omit<IButtonButtonProps, 'size' | 'variant' | 'emphasis' | 'iconClassName' | 'icon'>
  | Omit<IButtonAnchorProps, 'size' | 'variant' | 'emphasis' | 'iconClassName' | 'icon'>;
  /**
   * Size of the Search component
   */
  scale?: Extract<TSizeVariants, 'sm' | 'md' | 'lg' | 'xl'>;
}

export default InputSearch; 